import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  Search,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/datepicker-custom.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import ServiceSelection from "./calendar/ServiceSlection";
import SunbedSelection from "./SunbedSelection";
import CustomerSelection from "./calendar/CustomerSelection";
import SummaryStep from "./calendar/SummaryStep";
import AddCustomerStep from "./calendar/AddCustomerStep";
import PaymentStep from "./PaymentStep";
import FinalPaymentStep from "./FinalPaymentStep";
import PosPaymentStep from "./PosPaymentStep";
import BackButton from "./calendar/BackButton";
import ProductSelectionStep, { SelectedProduct } from "./calendar/ProductSelectionStep";
import TimeSpinner from "./ui/TimeSpinner";
import { useProducts } from "../context/ProductsContext";
import { useAuth } from "../auth/AuthContext";
import { useEmployees } from "../hooks/useEmployees";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";
import { getCustomerQuickDetails } from "../services/bookings";
import {
  getBusinessServices,
  getCreateFormData,
} from "../services/businessServicesApi";
import { storePosBooking } from "../services/posApi";
import { createDepositPaymentLink } from "../services/bookings";
import { useToast } from "../hooks/useToast";
import { checkEmployeeAvailability } from "../services/employeesApi";
import GlobalLoader from "./ui/GlobalLoader";
import { Employee } from "../types/employee";
import { Customer } from "../types/customer";
import { Service } from "../types/calendar";

function normalizeAppointmentSearch(raw: string): string {
  return (raw || "")
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "")
    .replace(/\ufeff/g, "")
    .trim()
    .toLowerCase();
}

function filterCalendarServicesBySearch(services: Service[], searchRaw: string): Service[] {
  const raw = normalizeAppointmentSearch(searchRaw);
  if (!raw) return services.slice();

  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return services.slice();

  const haystackFor = (s: Service): string => {
    const r = s as Service & {
      category?: { name?: string };
      category_name?: string;
    };
    const parts = [
      s.name,
      s.categoryName,
      typeof r.category === "object" && r.category?.name ? r.category.name : undefined,
      typeof r.category === "string" ? r.category : undefined,
      r.category_name,
    ];
    return parts
      .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
      .join(" ")
      .toLowerCase();
  };

  return services.filter((s) => {
    const haystack = haystackFor(s);
    return tokens.every((t) => haystack.includes(t));
  });
}

interface AppointmentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  staffList: { id: number; name: string; color: string }[];
  // Context Mode
  mode?: "dashboard" | "calendar" | "bookings";
  initialServiceType?: "Services" | "Sunbeds";

  // Calendar Context Props (Optional)
  stylist?: string;
  appointmentTime?: string;
  appointmentDate?: string | Date;
  services?: Service[];
  bookingData?: any;
  onRebook?: () => void;
  setFilteredServices?: (services: Service[]) => void;
  servicesLoading?: boolean;
  onRefresh?: () => void;
  preSelectedCustomer?: Customer | null;
  preSelectedServices?: Service[];
  rebookMode?: boolean;
}

const AppointmentSidebar: React.FC<AppointmentSidebarProps> = ({
  isOpen,
  onClose,
  staffList,
  mode = "calendar",
  initialServiceType = "Services",
  stylist,
  appointmentTime,
  appointmentDate,
  services: calendarServices,
  bookingData,
  onRebook = () => { },
  setFilteredServices: setCalendarFilteredServices,
  servicesLoading: calendarServicesLoading,
  onRefresh,
  preSelectedCustomer,
  preSelectedServices,
  rebookMode = false,
}) => {
  const { user } = useAuth();
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "";
  const toast = useToast();
  const { setProducts } = useProducts();
  const { employees } = useEmployees();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [customerDetails, setCustomerDetails] = useState<Customer | null>(null);
  const [selectedStylistId, setSelectedStylistId] = useState<number | null>(
    null,
  );
  const [selectedStylist, setSelectedStylist] = useState<string>("");
  const [selectedDateTime, setSelectedDateTime] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
  });
  const [step, setStep] = useState<
    | "selection"
    | "customer"
    | "summary"
    | "payment"
    | "finalPayment"
    | "addCustomer"
    | "posPayment"
    | "productSelection"
  >("selection");
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [tip, setTip] = useState<number>(0);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [isAddingService, setIsAddingService] = useState(false);
  const [dashboardFilteredServices, setDashboardFilteredServices] = useState<
    Service[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [allTaxes, setAllTaxes] = useState<any[]>([]);
  const [selectionMode, setSelectionMode] = useState<"Services" | "Sunbeds">(
    initialServiceType,
  );
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [recommendedSlots, setRecommendedSlots] = useState<string[]>([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [selectedConsultationFormIds, setSelectedConsultationFormIds] = useState<number[]>([]);
  const [serviceSearchQuery, setServiceSearchQuery] = useState("");

  const unfilteredServicesForSelection = useMemo(
    () => (mode === "calendar" ? calendarServices : dashboardFilteredServices) || [],
    [mode, calendarServices, dashboardFilteredServices],
  );

  const filteredServicesForSelection = useMemo(
    () => filterCalendarServicesBySearch(unfilteredServicesForSelection, serviceSearchQuery),
    [unfilteredServicesForSelection, serviceSearchQuery],
  );

  const extractConsultationFormIds = (bookingData: any): number[] => {
    if (!bookingData) return [];

    // Try all possible locations for the forms array
    const data = bookingData.booking || bookingData.rawBooking || bookingData;

    if (Array.isArray(data.consultation_form_response)) {
      return data.consultation_form_response
        .map((r: any) => Number(r.form_id || r.id))
        .filter(Boolean);
    }

    if (Array.isArray(data.consultation_forms)) {
      return data.consultation_forms
        .map((f: any) => Number(f.id))
        .filter(Boolean);
    }

    // Deep check if it's nested somewhere else (extreme case)
    if (data.booking && Array.isArray(data.booking.consultation_form_response)) {
      return data.booking.consultation_form_response.map((r: any) => Number(r.form_id || r.id)).filter(Boolean);
    }

    return [];
  };

  // Sync state when sidebar opens or props change
  useEffect(() => {
    if (isOpen) {
      setServiceSearchQuery("");
      setSelectionMode(initialServiceType || "Services");
      const initialStylist =
        stylist || (staffList && staffList.length > 0 ? staffList[0].name : "");
      setSelectedStylist(initialStylist);

      // Find the ID of the initial stylist to pass to ServiceSelection
      const initialStylistObj = staffList.find(
        (s) => s.name === initialStylist,
      );
      if (initialStylistObj) {
        setSelectedStylistId(initialStylistObj.id);
      }

      let currentInitDate = selectedDateTime.toLocaleDateString("en-CA");
      let currentInitTime = selectedDateTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      if (mode === "calendar") {
        if (rebookMode && bookingData) {
          setSelectedServices(
            bookingData.services ||
            (bookingData.service ? [bookingData.service] : []),
          );
          setSelectedCustomer(bookingData.customer);
          setSelectedConsultationFormIds(extractConsultationFormIds(bookingData));
          // if (bookingData.date) {
          //   const parsedDate = new Date(bookingData.date);
          //   if (!isNaN(parsedDate.getTime())) {
          //     setSelectedDateTime(parsedDate);
          //   }
          // }
          // In rebook mode, we want to default to NOW (or passed appointmentDate), not the old booking date.
          // So we skip setting selectedDateTime from bookingData here.
          setStep("summary");
        } else if (bookingData) {
          setSelectedServices(
            bookingData.services ||
            (bookingData.service ? [bookingData.service] : []),
          );
          setSelectedCustomer(bookingData.customer);
          setSelectedConsultationFormIds(extractConsultationFormIds(bookingData));
          setStep("summary");
        } else {
          setSelectedServices([]);
          setSelectedCustomer(null);
          setStep("selection");
        }
        if (appointmentDate) {
          let baseDate: Date;
          if (
            typeof appointmentDate === "string" &&
            /^\d{4}-\d{2}-\d{2}$/.test(appointmentDate)
          ) {
            const [y, m, d] = appointmentDate.split("-").map(Number);
            baseDate = new Date(y, m - 1, d, 12, 0, 0); // Noon Local
          } else {
            const dateToParse =
              typeof appointmentDate === "string"
                ? appointmentDate.replace(" ", "T")
                : appointmentDate;
            baseDate = new Date(dateToParse);
          }

          if (appointmentTime) {
            const match = appointmentTime.match(/(\d+):(\d+)\s*(am|pm)/i);
            if (match) {
              let [_, hours, minutes, ampm] = match;
              let h = parseInt(hours);
              const mPart = parseInt(minutes);
              if (ampm.toLowerCase() === "pm" && h < 12) h += 12;
              if (ampm.toLowerCase() === "am" && h === 12) h = 0;
              baseDate.setHours(h, mPart, 0, 0);
            }
          }

          if (!isNaN(baseDate.getTime())) {
            setSelectedDateTime(baseDate);
            currentInitDate = baseDate.toLocaleDateString("en-CA");
          }
          currentInitTime = appointmentTime || currentInitTime;
        }
      } else {
        setSelectedServices(preSelectedServices || []);
        setSelectedCustomer(preSelectedCustomer || null);
        setStep("selection");
        const now = new Date();
        setSelectedDateTime(now);
        currentInitDate = now.toLocaleDateString("en-CA");
        currentInitTime = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      handleStylistChange(initialStylist, currentInitDate, currentInitTime);
    } else {
      // Reset on close
      setServiceSearchQuery("");
      setProducts([]);
      setSelectedCustomer(null);
      setCustomerDetails(null);
      setIsAddingService(false);
      setSelectedConsultationFormIds([]);
      setSelectedProducts([]);
    }
  }, [
    isOpen,
    initialServiceType,
    mode,
    stylist,
    appointmentDate,
    bookingData,
    preSelectedCustomer,
    preSelectedServices,
    rebookMode,
  ]);

  const handleStylistChange = async (
    stylistName: string,
    overrideDate?: string,
    overrideTime?: string,
  ) => {
    setServiceSearchQuery("");
    setSelectedStylist(stylistName);
    const employee = employees.find(
      (emp: Employee) => emp.name === stylistName,
    );
    const employeeId = employee?.id
      ? Number(employee.id)
      : staffList.find((s) => s.name === stylistName)?.id || 1;
    setSelectedStylistId(employeeId);

    const dateStr =
      overrideDate || selectedDateTime.toISOString().split("T")[0];
    const timeStr =
      overrideTime ||
      selectedDateTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

    setLoading(true);
    try {
      const allServices = await getBusinessServices();
      const formData = await getCreateFormData();
      setAllTaxes(formData.taxes || []);
      // Store all services with their providing staff IDs
      const mappedServices = allServices.map((s) => ({
        id: s.id,
        name: s.name,
        duration: s.durationLabel,
        price: String(s.price),
        company_id: Number((s.raw as any)?.company_id || 0),
        staffIds: s.staff.map((m) => Number(m.id)),
        categoryId: (s.raw as any)?.category_id,
        categoryName: s.categoryName,
        locationId: (s.raw as any)?.location_id,
        description: (s.raw as any)?.description,
        discount: (s.raw as any)?.discount,
        discount_type: (s.raw as any)?.discount_type,
        service_type: (s.raw as any)?.service_type,
        taxes: (s.raw as any)?.taxes,
        tax_ids:
          (s.raw as any)?.service_taxes?.map((t: any) =>
            Number(t.tax_id || t.id),
          ) ||
          (s.raw as any)?.tax_services?.map((t: any) =>
            Number(t.tax_id || t.id),
          ) ||
          [],
        service_taxes: (s.raw as any)?.service_taxes,
        tax_services: (s.raw as any)?.tax_services,
      }));

      if (mode === "calendar" && setCalendarFilteredServices) {
        setCalendarFilteredServices(mappedServices);
      } else {
        setDashboardFilteredServices(mappedServices);
      }

      // Enrich selectedServices with fresh tax data if in rebook mode
      if (rebookMode) {
        setSelectedServices((prev) =>
          prev.map((s) => {
            const sId = String(s.id).replace(/^(service-|sunbed-)/, "");
            const match = mappedServices.find((m) => String(m.id) === sId);
            if (match) {
              return {
                ...s,
                tax_ids: match.tax_ids,
                service_taxes: match.service_taxes,
                taxes: match.taxes,
                // Ensure we have tax data to display
              };
            }
            return s;
          }),
        );
      }
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = () => {
    if (selectedServices.length > 0 && selectedCustomer) {
      setStep("posPayment");
    }
  };

  const goBack = () => {
    const backMap: Record<string, any> = {
      customer: "selection",
      addCustomer: "customer",
      summary: preSelectedCustomer ? "selection" : "customer",
      payment: "summary",
      finalPayment: "payment",
      posPayment: "summary",
      productSelection: "summary",
    };
    if (backMap[step]) {
      if (step === "posPayment") setProducts([]);
      setStep(backMap[step]);
    } else if (step === "selection" && isAddingService) {
      setIsAddingService(false);
      setStep("summary");
    }
  };

  const handlePosBack = () => {
    setStep("summary");
    setProducts([]);
  };

  // Fetch detailed customer info when reaching summary
  useEffect(() => {
    const fetchDetails = async () => {
      if (step === "summary" && selectedCustomer) {
        // If it's a walk-in customer, clear the detailed info and return
        if (selectedCustomer.id === 0) {
          setCustomerDetails(null);
          return;
        }

        // If current details don't match the selected customer ID, clear them
        // to avoid showing stale info while fetching
        if (customerDetails && customerDetails.id !== selectedCustomer.id) {
          setCustomerDetails(null);
        }

        try {
          const response = await getCustomerQuickDetails(selectedCustomer.id);
          if (response.success && response.data) {
            setCustomerDetails({
              id: response.data.id,
              name: response.data.name,
              email: response.data.email || "",
              dob: response.data.date_of_birth || undefined,
              phone: response.data.formatted_mobile || undefined,
              user_image_url: response.data.user_image_url || undefined,
            });
          }
        } catch (err) {
          console.error("Failed to fetch customer quick details:", err);
        }
      }
    };
    fetchDetails();
  }, [step, selectedCustomer]);

  // Check employee availability when factors change
  useEffect(() => {
    const checkAvailability = async () => {
      // Need at least one service and a stylist to check
      if (
        !selectedStylistId ||
        selectedServices.length === 0 ||
        selectionMode === "Sunbeds"
      ) {
        setIsAvailable(null);
        setAvailabilityMessage("");
        return;
      }

      setIsCheckingAvailability(true);
      try {
        const dateStr = selectedDateTime.toLocaleDateString("en-CA");
        const timeStr = selectedDateTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        const payload = {
          date: dateStr,
          time: timeStr,
          employee_id: selectedStylistId,
          cart_services_data: selectedServices.map((s) => {
            let id = s.id;
            if (typeof id === "string" && id.startsWith("service-")) {
              id = id.replace("service-", "");
            }
            return id;
          }),
        };

        const res = await checkEmployeeAvailability(payload);
        setIsAvailable(res.available);
        setRecommendedSlots(res.available_times || []);
        setAvailabilityMessage(
          res.available
            ? "Employee is available"
            : res.message || "Employee not available at this time"
        );
      } catch (err) {
        console.error("Availability check failed:", err);
        setIsAvailable(null);
        setAvailabilityMessage("");
      } finally {
        setIsCheckingAvailability(false);
      }
    };

    const debounceId = setTimeout(checkAvailability, 600);
    return () => clearTimeout(debounceId);
  }, [
    selectedStylistId,
    selectedDateTime,
    selectedServices,
    appointmentTime,
    selectionMode,
  ]);

  const handleTimeSelect = (timeStr: string) => {
    // Parse time like "10:15 am" or "10:15am"
    const match = timeStr.match(/(\d+):(\d+)\s*(am|pm)/i);
    if (!match) return;

    let [_, hours, minutes, ampm] = match;
    let h = parseInt(hours);
    const m = parseInt(minutes);
    if (ampm.toLowerCase() === "pm" && h < 12) h += 12;
    if (ampm.toLowerCase() === "am" && h === 12) h = 0;

    const newDate = new Date(selectedDateTime);
    newDate.setHours(h, m, 0, 0);
    setSelectedDateTime(newDate);
    setIsAvailable(true); // Optimistic
  };

  const currentStepIndex =
    step === "selection"
      ? 0
      : step === "customer" || step === "addCustomer"
        ? 1
        : step === "summary"
          ? 2
          : 3;
  const steps = ["Selection", "Customer", "Summary", "Payment"];

  // --- Render Helpers ---

  const getEffectiveTime = () => {
    if (rebookMode) {
      return selectedDateTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    return mode === "calendar"
      ? appointmentTime!
      : selectedDateTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
  };

  const renderSelectionStep = () => {
    if (selectionMode === "Sunbeds") {
      return (
        <SunbedSelection
          setSelectedServices={setSelectedServices}
          setStep={setStep}
          isAddingService={isAddingService}
          setIsAddingService={setIsAddingService}
          disabledServices={selectedServices}
        />
      );
    }

    if (loading || calendarServicesLoading) {
      return (
        <div className="flex flex-1 items-center justify-center h-[300px]">
          <GlobalLoader
            initialPageLoad={false}
            compensateSidebar={false}
            description="Loading services..."
          />
        </div>
      );
    }

    return (
      <ServiceSelection
        services={filteredServicesForSelection}
        searchQuery={serviceSearchQuery}
        catalogServiceCount={unfilteredServicesForSelection.length}
        setSelectedServices={setSelectedServices}
        setStep={(nextStep) => {
          if (nextStep === "customer" && preSelectedCustomer) {
            setStep("summary");
          } else {
            setStep(nextStep);
          }
        }}
        isAddingService={isAddingService}
        setIsAddingService={setIsAddingService}
        disabledServices={selectedServices}
        selectedStylistId={selectedStylistId || undefined}
        selectedStylistName={selectedStylist || undefined}
      />
    );
  };

  const renderStepContent = () => {
    const commonProps = {
      selectedServices,
      selectedCustomer: selectedCustomer!,
      appointmentTime: getEffectiveTime(),
      tip,
      setTip,
      goBack,
      staffList,
      stylist: selectedStylist,
    };

    const motionProps = {
      initial: { opacity: 0, x: 10 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -10 },
      transition: { duration: 0.3 },
      className: cn(
        "flex-1 overflow-y-auto",
        step === "payment" || step === "finalPayment" || step === "summary"
          ? ""
          : "p-4 sm:p-6",
      ),
    };

    switch (step) {
      case "selection":
        return (
          <motion.div key="selection" {...motionProps}>
            {renderSelectionStep()}
          </motion.div>
        );

      case "customer":
        return (
          <motion.div key="customer" {...motionProps}>
            <CustomerSelection
              setSelectedCustomer={setSelectedCustomer}
              setStep={setStep}
              selectedServices={selectedServices}
            />
          </motion.div>
        );

      case "addCustomer":
        return (
          <motion.div key="addCustomer" {...motionProps}>
            <AddCustomerStep
              setSelectedCustomer={setSelectedCustomer}
              setStep={setStep}
            />
          </motion.div>
        );

      case "summary": {
        if (!selectedCustomer) return null;

        const handleSaveAsBooked = async () => {
          if (!selectedCustomer || selectedServices.length === 0) return;
          try {
            const serviceIds = selectedServices
              .filter((s) => !s.sunbedId && !s.membershipPlanId)
              .map((s) => {
                let id = s.id;
                if (typeof id === "string" && id.startsWith("service-")) {
                  id = id.replace("service-", "");
                }
                return id;
              });

            const serviceQuantities = selectedServices
              .filter((s) => !s.sunbedId && !s.membershipPlanId)
              .map(() => 1);

            const servicePrices = selectedServices
              .filter((s) => !s.sunbedId && !s.membershipPlanId)
              .map((s) =>
                typeof s.price === "string"
                  ? parseFloat(s.price.replace(/[£$€]/g, ""))
                  : Number(s.price || 0),
              );

            const sunbedPrices = selectedServices
              .filter((s) => !!s.sunbedId || !!s.membershipPlanId)
              .map((s) =>
                typeof s.price === "string"
                  ? parseFloat(s.price.replace(/[£$€]/g, ""))
                  : Number(s.price || 0),
              );

            const subtotal = [...servicePrices, ...sunbedPrices].reduce(
              (a, b) => a + b,
              0,
            );

            const serviceTaxPercents = selectedServices
              .filter((s) => !s.sunbedId && !s.membershipPlanId)
              .map((s) => {
                const embeddedTaxes = s.service_taxes || s.tax_services;
                if (
                  embeddedTaxes &&
                  Array.isArray(embeddedTaxes) &&
                  embeddedTaxes.length > 0
                ) {
                  return embeddedTaxes.reduce(
                    (sum, item) => sum + (item.tax?.percent || 0),
                    0,
                  );
                }
                if (!s.tax_ids || !allTaxes) return 0;
                return allTaxes
                  .filter((t) => s.tax_ids?.includes(Number(t.id)))
                  .reduce((sum, t) => sum + (t.percent || 0), 0);
              });

            const serviceTotalWithDiscount = selectedServices
              .filter((s) => !s.sunbedId && !s.membershipPlanId)
              .map((s) => {
                const price =
                  typeof s.price === "string"
                    ? parseFloat(s.price.replace(/[£$€]/g, ""))
                    : Number(s.price || 0);
                const discount = parseFloat(String((s as any).discount)) || 0;
                const discountType = (s as any).discount_type;
                if (discount > 0) {
                  if (discountType === "percent")
                    return price - (price * discount) / 100;
                  else return price - discount;
                }
                return price;
              });

            const serviceTaxes = serviceTotalWithDiscount.map(
              (finalPrice, idx) => {
                return (finalPrice * serviceTaxPercents[idx]) / 100;
              },
            );

            const calculatedTotalTax = serviceTaxes.reduce((a, b) => a + b, 0);
            const totalAmount =
              [...serviceTotalWithDiscount, ...sunbedPrices].reduce(
                (a, b) => a + b,
                0,
              ) + calculatedTotalTax;

            const formatToHiA = (timeStr: string): string => {
              if (!timeStr) return "";
              const normalized = timeStr.split("–")[0].replace(".", ":").trim();
              if (
                normalized.toLowerCase().includes("am") ||
                normalized.toLowerCase().includes("pm")
              ) {
                const parts = normalized.split(" ");
                const timePart = parts[0];
                const ampm =
                  parts[1] ||
                  (normalized.toLowerCase().includes("pm") ? "pm" : "am");
                const [h, m] = timePart.split(":");
                return `${h.padStart(2, "0")}:${(m || "00").padEnd(2, "0")} ${ampm.toLowerCase()}`;
              }
              return timeStr;
            };

            const finalPosDate = selectedDateTime.toLocaleDateString("en-CA");
            const finalPosTime = formatToHiA(getEffectiveTime());

            // Prepare d-m-Y format
            const parts = finalPosDate.split("-");
            const dateDMY = `${parts[2]}-${parts[1]}-${parts[0]}`;

            const payload = {
              cart_services: serviceIds.length > 0 ? serviceIds : undefined,
              cart_quantity:
                serviceQuantities.length > 0 ? serviceQuantities : undefined,
              cart_prices: servicePrices.length > 0 ? servicePrices : undefined,
              tax_percent:
                serviceTaxPercents.length > 0 ? serviceTaxPercents : undefined,
              calendar_sunbeds: selectedServices
                .filter((s) => s.sunbedId && !s.membershipPlanId)
                .map((s) => ({
                  id: s.sunbedId!,
                  pricing: s.pricing || [],
                  selectedPricingId: s.selectedPricingId!,
                  selectedMinutes: s.selectedMinutes,
                  company_id: s.company_id || user?.company?.id,
                })),
              calendar_memberships: selectedServices
                .filter((s) => s.membershipPlanId)
                .map((s) => ({
                  plan_id: Number(s.membershipPlanId),
                  price:
                    typeof s.price === "string"
                      ? parseFloat(s.price.replace(/[£$€]/g, ""))
                      : Number(s.price || 0),
                })),
              location: user?.company?.location_id || 1,
              pos_date: finalPosDate,
              pos_time: finalPosTime,
              date: dateDMY,
              time: finalPosTime,
              posTime: finalPosTime,
              user_id:
                !selectedCustomer.id || selectedCustomer.id === 0
                  ? 4
                  : selectedCustomer.id,
              payments: [{ type: "cash" as const, amount: 0 }],
              payment_gateway: "Cash",
              pending_amount: Number(totalAmount.toFixed(2)),
              amount_paid: 0,
              change_due: "0.00",
              type: "pay_later" as const,
              serviceType: "offline" as const,
              total_tax: Number(calculatedTotalTax.toFixed(2)),
              cart_discount: Number(
                (subtotal - (totalAmount - calculatedTotalTax)).toFixed(2),
              ),
              coupon_id: "",
              coupon_amount: "",
              coupon: "",
              uuid: null,
              transaction_id: null,
              // Per-service employee array: use each service's assigned stylist (set at add-time),
              // falling back to the current dropdown selection, then first staff member.
              employee: (() => {
                const fallback = selectedStylistId || (staffList.length > 0 ? staffList[0].id : null);
                const ids = selectedServices
                  .filter((s) => !s.sunbedId && !s.membershipPlanId)
                  .map((s) => s.assignedStylistId ?? fallback ?? null);
                return ids.length > 0 ? ids : (fallback ? [fallback] : undefined);
              })(),
              consultation_form_ids: selectedConsultationFormIds,
              // Products
              ...(selectedProducts.length > 0 && {
                product_cart_services: selectedProducts.map((p) => p.id),
                product_cart_quantity: selectedProducts.map((p) => p.quantity),
                product_cart_prices: selectedProducts.map(
                  (p) => p.discountedPrice ?? p.price,
                ),
                in_store_usage: selectedProducts.map(() => 0),
              }),
            };

            const result = await storePosBooking(payload);
            toast.success("Booking saved successfully!");

            const bookingId = result?.booking?.id ?? result?.booking_id;
            const depositDue = Number(
              (result?.booking as any)?.deposit ?? 0
            );
            if (bookingId && depositDue >= 0.5) {
              try {
                const linkRes = await createDepositPaymentLink(
                  bookingId,
                  true
                );
                toast.success(
                  linkRes?.message ||
                    "Deposit payment link sent to customer."
                );
                if (
                  linkRes?.payment_url &&
                  navigator?.clipboard?.writeText
                ) {
                  try {
                    await navigator.clipboard.writeText(linkRes.payment_url);
                  } catch {
                    /* ignore */
                  }
                }
              } catch (linkErr: any) {
                toast.error(
                  linkErr?.message ||
                    "Booking saved, but deposit link could not be sent."
                );
              }
            }

            if (onRefresh) onRefresh();
            onClose();
          } catch (error: any) {
            console.error("Save as booked error details:", {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
            });
            const errorMessage =
              error.response?.data?.message ||
              error.message ||
              "Failed to save booking.";
            toast.error(errorMessage);
          }
        };

        return (
          <motion.div key="summary" {...motionProps} className="h-full">
            <SummaryStep
              selectedServices={selectedServices}
              selectedCustomer={customerDetails || selectedCustomer}
              bookingData={rebookMode ? undefined : bookingData}
              onRebook={onRebook}
              setIsAddingService={setIsAddingService}
              setSelectionMode={setSelectionMode}
              selectionMode={selectionMode}
              setStep={(ns) => {
                if (ns === "payment") handlePayNow();
                else setStep(ns as any);
              }}
              appointmentTime={getEffectiveTime()}
              tip={tip}
              setTip={setTip}
              staffList={staffList}
              stylist={selectedStylist}
              onSaveAsBooked={handleSaveAsBooked}
              onRefresh={onRefresh}
              setSelectedServices={setSelectedServices}
              onClose={onClose}
              allTaxes={allTaxes}
              rebookMode={rebookMode}
              selectedDateTime={selectedDateTime}
              setSelectedDateTime={setSelectedDateTime}
              onStylistChange={handleStylistChange}
              isAvailable={isAvailable}
              isCheckingAvailability={isCheckingAvailability}
              recommendedSlots={recommendedSlots}
              consultationFormIds={selectedConsultationFormIds}
              onConsultationFormsChange={setSelectedConsultationFormIds}
              selectedProducts={selectedProducts}
              onAddProducts={() => setStep("productSelection")}
              onRemoveProduct={(id) =>
                setSelectedProducts((prev) => prev.filter((p) => String(p.id) !== String(id)))
              }
            />
          </motion.div>
        );
      }

      case "payment": {
        if (!selectedCustomer) return null;
        return (
          <motion.div key="payment" {...motionProps}>
            <PaymentStep
              selectedCustomer={customerDetails || selectedCustomer}
              selectedServices={selectedServices}
              appointmentTime={getEffectiveTime()}
              tip={tip}
              setTip={setTip}
              goToFinalPayment={() => setStep("finalPayment")}
              bookingId={rebookMode ? undefined : bookingData?.id}
              goBack={() => setStep("summary")}
              staffList={staffList}
              stylist={selectedStylist}
              consultation_form_ids={selectedConsultationFormIds}
            />
          </motion.div>
        );
      }

      case "finalPayment": {
        if (!selectedCustomer) return null;
        return (
          <motion.div key="finalPayment" {...motionProps}>
            <FinalPaymentStep
              selectedCustomer={customerDetails || selectedCustomer}
              selectedServices={selectedServices}
              appointmentTime={getEffectiveTime()}
              onRebook={onRebook}
              bookingId={rebookMode ? undefined : bookingData?.id}
              goBack={() => setStep("payment")}
              staffList={staffList}
              stylist={selectedStylist}
              consultation_form_ids={selectedConsultationFormIds}
            />
          </motion.div>
        );
      }

      case "posPayment": {
        if (!selectedCustomer || selectedServices.length === 0) return null;

        const totalServicePrice = selectedServices.reduce(
          (sum, s) =>
            sum +
            (typeof s.price === "string"
              ? parseFloat(s.price.replace(/[£$€]/g, ""))
              : Number(s.price)),
          0,
        );

        return (
          <motion.div key="posPayment" {...motionProps} className="h-full">
            <PosPaymentStep
              selectedService={{
                id: selectedServices[0].id,
                name: selectedServices.map((s) => s.name).join(", "),
                duration: selectedServices.map((s) => s.duration).join(", "),
                price: `${currencySymbol}${totalServicePrice.toFixed(2)}`,
              }}
              selectedCustomer={selectedCustomer.name}
              appointmentTime={getEffectiveTime()}
              tip={tip}
              setTip={setTip}
              goToFinalPayment={() => setStep("finalPayment")}
              onClose={onClose}
              mode="paynow"
              locationId={user?.company?.location_id || 1}
              userId={
                !selectedCustomer.id || selectedCustomer.id === 0
                  ? 4
                  : selectedCustomer.id
              }
              initialAmountPaid={bookingData?.amount_paid || 0}
              initialPendingAmount={
                bookingData?.pending_amount !== undefined &&
                  bookingData?.pending_amount !== null
                  ? Math.max(0, Number(bookingData.pending_amount))
                  : bookingData?.amount_to_pay !== undefined &&
                      bookingData?.amount_paid !== undefined
                    ? Math.max(
                        0,
                        Number(bookingData.amount_to_pay) -
                          Number(bookingData.amount_paid),
                      )
                    : undefined
              }
              paymentBookingId={rebookMode ? undefined : bookingData?.id}
              services={selectedServices
                .filter((s) => !s.sunbedId && !s.membershipPlanId)
                .map((s) => {
                  const originalPrice =
                    typeof s.price === "string"
                      ? parseFloat(s.price.replace(/[£$€]/g, ""))
                      : Number(s.price || 0);
                  const discount = parseFloat(String((s as any).discount)) || 0;
                  const discountType = (s as any).discount_type;
                  let finalPrice = originalPrice;
                  if (discount > 0) {
                    if (discountType === "percent")
                      finalPrice =
                        originalPrice - (originalPrice * discount) / 100;
                    else finalPrice = originalPrice - discount;
                  }

                  // Calculate tax percent using embedded taxes or allTaxes fallback
                  const embeddedTaxes = s.service_taxes || s.tax_services;
                  let taxPercent = 0;
                  if (
                    embeddedTaxes &&
                    Array.isArray(embeddedTaxes) &&
                    embeddedTaxes.length > 0
                  ) {
                    taxPercent = embeddedTaxes.reduce(
                      (sum, item) => sum + (item.tax?.percent || 0),
                      0,
                    );
                  } else if (s.tax_ids && allTaxes) {
                    taxPercent = allTaxes
                      .filter((t) => s.tax_ids?.includes(Number(t.id)))
                      .reduce((sum, t) => sum + (t.percent || 0), 0);
                  }

                  return {
                    id: String(s.id)
                      .replace("service-", "")
                      .replace("sunbed-", ""),
                    quantity: 1,
                    price: finalPrice,
                    taxPercent: taxPercent,
                  };
                })}
              sunbeds={selectedServices
                .filter((s) => s.sunbedId && !s.membershipPlanId)
                .map((s) => ({
                  id: String(s.sunbedId!).replace("sunbed-", ""),
                  pricing: s.pricing || [],
                  selectedPricingId: s.selectedPricingId!,
                  selectedMinutes: s.selectedMinutes,
                  company_id: s.company_id || user?.company?.id,
                }))}
              memberships={selectedServices
                .filter((s) => s.membershipPlanId)
                .map((s) => ({
                  plan_id: Number(s.membershipPlanId),
                  price:
                    typeof s.price === "string"
                      ? parseFloat(s.price.replace(/[£$€]/g, ""))
                      : Number(s.price || 0),
                }))}
              employees={(() => {
                const fallback = selectedStylistId || (staffList.length > 0 ? staffList[0].id : null);
                const ids = selectedServices
                  .filter((s) => !s.sunbedId && !s.membershipPlanId)
                  .map((s) => s.assignedStylistId ?? fallback ?? null);
                return ids.length > 0 ? ids : (fallback ? [fallback] : undefined);
              })()}
              cartProducts={selectedProducts.length > 0 ? selectedProducts.map((p) => ({
                id: String(p.id),
                quantity: p.quantity,
                price: p.discountedPrice ?? p.price,
                taxPercent: 0,
              })) : undefined}
              posDate={selectedDateTime.toLocaleDateString("en-CA")}
              onPaymentComplete={() => {
                setProducts([]);
                if (onRefresh) onRefresh();
                onClose();
              }}
              isEmbedded={true}
              onBack={handlePosBack}
              consultation_form_ids={selectedConsultationFormIds}
            />
          </motion.div>
        );
      }

      case "productSelection":
        return (
          <motion.div key="productSelection" {...motionProps}>
            <ProductSelectionStep
              selectedProducts={selectedProducts}
              onConfirm={(products) => {
                setSelectedProducts(products);
                setStep("summary");
              }}
              onBack={() => setStep("summary")}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[999]"
            onClick={step === "posPayment" ? handlePosBack : onClose}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%", width: "500px" }}
            animate={{
              x: 0,
              width:
                step === "summary" ||
                  step === "payment" ||
                  step === "finalPayment" ||
                  step === "posPayment"
                  ? window.innerWidth < 1024
                    ? "100%"
                    : "60%"
                  : window.innerWidth < 1024
                    ? "100%"
                    : "500px",
            }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={cn(
              "fixed top-0 right-0 h-full bg-[#F9F9F9] shadow-[-10px_0_30px_rgba(0,0,0,0.05)] border-l border-gray-100 z-[1000] flex flex-col origin-right",
            )}
          >
            {/* Close Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
            >
              <X
                size={20}
                strokeWidth={3}
                className="group-hover:rotate-90 transition-transform duration-300"
              />
            </motion.button>

            {/* Header */}
            <AnimatePresence>
              {step !== "payment" &&
                step !== "finalPayment" &&
                step !== "summary" &&
                step !== "posPayment" &&
                step !== "productSelection" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white border-b border-gray-100 shadow-sm z-10 sticky top-0"
                  >
                    <div className="px-4 sm:px-8 pt-10 pb-6 flex items-center justify-between">
                      <div>
                        <h2 className="text-xl sm:text-[27px] font-[600] text-[#402B69] leading-tight pr-8 lg:pr-0">
                          {step === "addCustomer"
                            ? "Add Customer"
                            : selectionMode === "Sunbeds"
                              ? "Add Sunbed Session"
                              : "Add Appointment"}
                        </h2>
                        <p className="text-[10px] sm:text-[11px] text-gray-400 mt-1 font-semibold uppercase">
                          Step {currentStepIndex + 1} of 4:{" "}
                          {steps[currentStepIndex]}
                        </p>
                      </div>
                      <div>
                        {(step !== "selection" ||
                          (step === "selection" && isAddingService)) && (
                            <BackButton onClick={goBack} />
                          )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 px-4 sm:px-8 mb-6">
                      {[0, 1, 2, 3].map((s) => (
                        <div
                          key={s}
                          className={cn(
                            "h-1.5 flex-1 rounded-full transition-all duration-500",
                            s <= currentStepIndex
                              ? "bg-[#885ABB]"
                              : "bg-gray-100",
                          )}
                        />
                      ))}
                    </div>

                    {selectionMode !== "Sunbeds" && (
                      <div className="px-4 sm:px-8 pb-8 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <label className="text-[11px] font-semibold text-gray-400 uppercase pl-1">
                              Stylist
                            </label>
                            <Select
                              value={selectedStylist}
                              onValueChange={handleStylistChange}
                            >
                              <SelectTrigger className="h-12 border border-gray-200 bg-white rounded-xl text-sm font-semibold text-[#402B69] shadow-sm hover:border-[#885ABB] transition-all">
                                <SelectValue placeholder="Select stylist" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[9999]">
                                {staffList.map((s) => (
                                  <SelectItem key={s.name} value={s.name}>
                                    {s.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex flex-col gap-2">
                            <label className="text-[11px] font-semibold text-gray-400 uppercase pl-1">
                              Date & Time
                            </label>
                            {mode === "dashboard" ? (
                              <DatePicker
                                selected={selectedDateTime}
                                onChange={(date: Date | null) =>
                                  date && setSelectedDateTime(date)
                                }
                                showTimeInput
                                customTimeInput={<TimeSpinner />}
                                dateFormat="dd MMM yyyy, h:mm aa"
                                className="h-12 w-full border border-gray-100 bg-white rounded-xl text-sm font-bold text-[#402B69] px-4 shadow-sm hover:border-[#885ABB] transition-all focus:outline-none"
                                calendarClassName="custom-datepicker"
                                popperPlacement="bottom-end"
                                portalId="root"
                                renderCustomHeader={({
                                  date,
                                  decreaseMonth,
                                  increaseMonth,
                                }) => (
                                  <div className="flex items-center justify-between px-3 py-1 bg-[#885ABB] rounded-t-lg">
                                    <button
                                      onClick={decreaseMonth}
                                      type="button"
                                      className="p-1 text-white hover:bg-white/20 rounded-lg"
                                    >
                                      <ChevronLeft size={18} />
                                    </button>
                                    <span className="text-white font-semibold text-xs uppercase">
                                      {date.toLocaleString("default", {
                                        month: "long",
                                        year: "numeric",
                                      })}
                                    </span>
                                    <button
                                      onClick={increaseMonth}
                                      type="button"
                                      className="p-1 text-white hover:bg-white/20 rounded-lg"
                                    >
                                      <ChevronRight size={18} />
                                    </button>
                                  </div>
                                )}
                              />
                            ) : (
                              <div className="h-12 border border-gray-100 bg-gray-50/50 rounded-xl flex items-center px-3 sm:px-4 gap-2 text-xs sm:text-sm font-bold text-gray-500">
                                <CalendarIcon size={16} className="shrink-0" />
                                <span className="whitespace-nowrap">
                                  {new Date(
                                    appointmentDate!,
                                  ).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                  })}
                                </span>
                                <span className="w-px h-3 bg-gray-300 mx-0.5 sm:mx-1 shrink-0" />
                                <span className="whitespace-nowrap">
                                  {appointmentTime}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {step === "selection" &&
                          !loading &&
                          !calendarServicesLoading &&
                          unfilteredServicesForSelection.length > 0 && (
                            <div className="relative shrink-0 pt-1">
                              <Search
                                className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                                strokeWidth={2.25}
                                aria-hidden
                              />
                              <input
                                type="search"
                                value={serviceSearchQuery}
                                onChange={(e) =>
                                  setServiceSearchQuery(
                                    e.target.value.replace(/\u200b/g, "").replace(/\ufeff/g, ""),
                                  )
                                }
                                placeholder="Search by service or category…"
                                className="h-8 w-full rounded-lg border border-gray-200 bg-gray-50/80 py-1.5 pl-9 pr-3 text-xs text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#C4A6E1] focus:bg-white focus:ring-1 focus:ring-[#885ABB]/20"
                                autoComplete="off"
                                spellCheck={false}
                              />
                            </div>
                          )}
                      </div>
                    )}

                    {/* Availability Indicator */}
                    <AnimatePresence>
                      {selectedStylistId &&
                        selectedServices.length > 0 &&
                        selectionMode !== "Sunbeds" && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="px-4 sm:px-8 pb-4"
                          >
                            {isCheckingAvailability ? (
                              <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">
                                <Clock size={14} /> Checking availability...
                              </div>
                            ) : isAvailable === true ? (
                              <div className="flex items-center gap-2 text-[10px] font-bold text-green-500 uppercase tracking-widest">
                                <CheckCircle2 size={14} /> Employee is available
                              </div>
                            ) : isAvailable === false ? (
                              <div className="p-4 bg-red-50/50 border border-red-100 rounded-xl space-y-3">
                                <div className="flex items-center gap-2 text-red-600 font-bold text-xs uppercase tracking-tight">
                                  <AlertCircle size={16} /> {availabilityMessage || "Employee not available at this time"}
                                </div>
                                {recommendedSlots.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-[10px] text-red-400 font-bold uppercase tracking-wider">
                                      Recommended slots
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {recommendedSlots.map((time) => (
                                        <button
                                          key={time}
                                          type="button"
                                          onClick={() => handleTimeSelect(time)}
                                          className="px-3 py-1.5 bg-white border border-red-100 rounded-lg text-[11px] font-bold text-red-600 hover:bg-[#885ABB] hover:text-white hover:border-[#885ABB] transition-all shadow-sm"
                                        >
                                          {time}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </motion.div>
                        )}
                    </AnimatePresence>
                  </motion.div>
                )}
            </AnimatePresence>

            {/* Steps Body */}
            <div className="flex-1 overflow-hidden flex flex-col relative">
              <AnimatePresence mode="wait">
                {renderStepContent()}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AppointmentSidebar;
